import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, FileText, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { certError } from "@/lib/certs";
import { CertificatePreview } from "@/components/certs/CertificatePreview";
import { LogoManager } from "@/components/certs/LogoManager";
import { WhatsAppStatusCard } from "@/components/certs/WhatsAppStatusCard";

const F = ({ label, k, b, set, area, max, hint }) => (
  <div className={area ? "sm:col-span-2" : ""}>
    <Label className="text-xs">{label}</Label>
    {area ? <Textarea value={b[k]} maxLength={max} onChange={(e) => set(k, e.target.value)} className="min-h-[80px]" data-testid={`cert-set-${k}`} />
      : <Input value={b[k]} maxLength={max} onChange={(e) => set(k, e.target.value)} data-testid={`cert-set-${k}`} />}
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const Color = ({ label, k, b, set }) => (
  <div><Label className="text-xs">{label}</Label>
    <div className="flex gap-2"><input type="color" value={b[k]} onChange={(e) => set(k, e.target.value.toUpperCase())} className="h-10 w-12 cursor-pointer rounded border border-border bg-transparent" data-testid={`cert-set-${k}-picker`} />
      <Input value={b[k]} onChange={(e) => set(k, e.target.value)} maxLength={7} className="font-mono" data-testid={`cert-set-${k}`} /></div>
  </div>
);

const Pick = ({ label, k, b, set, opts }) => (
  <div><Label className="text-xs">{label}</Label>
    <Select value={b[k]} onValueChange={(v) => set(k, v)}><SelectTrigger data-testid={`cert-set-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
  </div>
);

export default function CertificateSettings() {
  const [data, setData] = useState(null);
  const [b, setB] = useState(null);
  const [kind, setKind] = useState("dp");
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/certificates/branding").then(({ data: d }) => { setData(d); setB(d.branding); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let u = null;
    setLogoUrl(null);
    if (data?.active_logo) {
      api.get(`/certificates/logo/${data.active_logo.id}/image`, { responseType: "blob" })
        .then(({ data: blob }) => { u = URL.createObjectURL(blob); setLogoUrl(u); }).catch(() => {});
    }
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [data]);

  if (!b) return null;
  const set = (k, v) => setB((x) => ({ ...x, [k]: v }));
  const dirty = JSON.stringify(b) !== JSON.stringify(data.branding);
  const save = async () => {
    setBusy(true);
    try { const { data: r } = await api.put("/certificates/branding", b); toast.success(r.changed ? `Saved as template v${r.template_version}. New certificates use it.` : "No changes"); load(); }
    catch (e) { toast.error(await certError(e)); }
    setBusy(false);
  };
  const pdf = async () => {
    const w = window.open("", "_blank");
    try {
      const { data: blob } = await api.post("/certificates/preview", { branding: b, kind }, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); toast.error(await certError(e)); }
  };
  const p = { b, set };

  return (
    <div className="space-y-4" data-testid="cert-settings">
      <LogoManager data={data} logoUrl={logoUrl} onChanged={load} onColors={(c) => setB((x) => ({ ...x, primary_color: c[0], accent_color: c[1] || x.accent_color }))} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <div className="rounded-xl border border-border bg-card p-5 xl:col-span-2">
          <h3 className="font-head text-sm font-semibold">Certificate Design <span className="ml-1 text-xs font-normal text-muted-foreground">template v{data.template_version}</span></h3>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <F label="Company Name" k="company_name" max={60} {...p} />
            <F label="Verification Badge Text" k="badge_text" max={24} {...p} hint="Shown only when verification is complete" />
            <F label="Delivery Partner Heading" k="dp_heading" max={60} {...p} />
            <F label="Franchise Partner Heading" k="fr_heading" max={60} {...p} />
            <F label="Delivery Partner Description" k="dp_description" max={420} area {...p} hint="[Delivery Partner Name] is replaced with the partner's name" />
            <F label="Franchise Partner Description" k="fr_description" max={420} area {...p} hint="[Franchise Partner Name] is replaced with the partner's name" />
            <Color label="Primary Colour" k="primary_color" {...p} />
            <Color label="Accent Colour" k="accent_color" {...p} />
            <F label="Authorized Signatory Name" k="signatory_name" max={60} {...p} hint="Leave blank to sign by hand" />
            <F label="Signatory Designation" k="signatory_designation" max={60} {...p} />
            <Pick label="Certificate Background" k="background" opts={[["white", "White"], ["cream", "Cream"], ["pattern", "Light pattern"]]} {...p} />
            <Pick label="Logo Position" k="logo_position" opts={[["center", "Top centre"], ["left", "Top left"]]} {...p} />
            <F label="Footer Text" k="footer_text" max={140} {...p} />
            <F label="Validity Statement (optional)" k="validity_text" max={160} {...p} />
            <F label="Verification Website (QR code link)" k="verify_base_url" max={200} {...p} hint="e.g. https://connecttym.online. Leave blank to use the address this CRM is opened from." />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy || !dirty} data-testid="cert-settings-save"><Save className="mr-1.5 h-4 w-4" /> Save Branding Settings</Button>
            <Button variant="outline" onClick={pdf} data-testid="cert-settings-preview-pdf"><FileText className="mr-1.5 h-4 w-4" /> Preview PDF</Button>
            <Button variant="ghost" onClick={() => setB({ ...data.defaults })} data-testid="cert-settings-defaults"><RotateCcw className="mr-1.5 h-4 w-4" /> Defaults</Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Changes apply to newly issued certificates. Issued certificates keep their original design unless reissued.</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 xl:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-head text-sm font-semibold">Live Preview {dirty && <span className="ml-1 text-xs font-normal text-amber-500">unsaved changes</span>}</h3>
            <Select value={kind} onValueChange={setKind}><SelectTrigger className="h-8 w-48" data-testid="cert-preview-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="dp">Delivery Partner</SelectItem><SelectItem value="franchise">Franchise Partner</SelectItem></SelectContent></Select>
          </div>
          <CertificatePreview b={b} kind={kind} logoUrl={logoUrl} />
          <p className="mt-2 text-[11px] text-muted-foreground">Approximate on-screen preview. Use Preview PDF for the exact print output.</p>
        </div>
      </div>
      <WhatsAppStatusCard />
    </div>
  );
}
